import { useCallback } from 'react'
import { TripService } from '@/modules/trips'
import { useAuth } from '@/modules/auth'
import { AddMemberDto, AddTagAlongDto, UpdateMemberRoleDto } from '@/shared/api'
import { MilitaryRolesApi } from '@/shared/api/militaryRoles'
import type { MilitaryRole } from '@/shared/api/militaryRoles'

export function useMemberOperations() {
  const { getAccessToken } = useAuth()
  
  const tripService = useCallback(() => new TripService(getAccessToken), [getAccessToken])
  const militaryRolesApi = useCallback(() => new MilitaryRolesApi(getAccessToken), [getAccessToken])

  const addMember = useCallback(async (tripId: string, memberData: AddMemberDto) => {
    const service = tripService()
    return service.addMember(tripId, memberData)
  }, [tripService])

  const addTagAlong = useCallback(async (
    tripId: string,
    parentMemberId: string,
    data: AddTagAlongDto
  ) => {
    const service = tripService()
    return service.addTagAlong(tripId, parentMemberId, data)
  }, [tripService])

  const updateMemberRole = useCallback(async (
    tripId: string,
    memberId: string,
    roleData: UpdateMemberRoleDto
  ) => {
    const service = tripService()
    return service.updateMemberRole(tripId, memberId, roleData)
  }, [tripService])

  const updateMilitaryRole = useCallback(async (
    tripId: string,
    memberId: string,
    militaryRole: MilitaryRole
  ) => {
    const api = militaryRolesApi()
    await api.updateMemberMilitaryRole(tripId, memberId, { military_role: militaryRole })
  }, [militaryRolesApi])

  const removeMember = useCallback(async (tripId: string, memberId: string) => {
    const service = tripService()
    return service.removeMember(tripId, memberId)
  }, [tripService])

  return {
    addMember,
    addTagAlong,
    updateMemberRole,
    updateMilitaryRole,
    removeMember
  }
}
